import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, Eye, EyeOff, Loader2, Lock, Mail,
} from 'lucide-react';
import { AuthShell } from './AuthShell';
import { useAiTrainerAuth } from '@/contexts/AiTrainerAuthContext';

/**
 * Trainer sign-in. Mirrors the kisiwa_management_dash login card —
 * brand eyebrow, Instrument-Serif title, icon-prefixed inputs, eye
 * toggle, error pill, gradient submit, footer link.
 */
const LoginPage: React.FC = () => {
  const { login, busy, error, clearError } = useAiTrainerAuth();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const onChange = (k: 'email' | 'password') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((p) => ({ ...p, [k]: e.target.value }));
      if (error) clearError();
    };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const user = await login(form);
      navigate(user.status === 'active' ? '/library' : '/pending', { replace: true });
    } catch { /* error already in context */ }
  };

  return (
    <AuthShell
      eyebrow="Trainer sign-in"
      title="Welcome back"
      subtitle="Sign in to upload past papers and watch ingestion"
      icon={<Lock size={18} />}
      footer={
        <p className="m-0 text-[13px] text-slate-500">
          New here?{' '}
          <Link
            to="/register"
            className="font-semibold text-[#0e9172] transition hover:text-[#0a7a5e] hover:underline"
          >
            Create an account
          </Link>
        </p>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        {/* Email */}
        <Field label="Email Address" icon={<Mail size={14} />}>
          <InputWrap leftIcon={<Mail size={16} />}>
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="trainer@institution.ac.ke"
              value={form.email}
              onChange={onChange('email')}
              className={inputCls(!!error)}
              autoFocus
            />
          </InputWrap>
        </Field>

        {/* Password — with forgot link on the same row as the label */}
        <Field
          label="Password"
          icon={<Lock size={14} />}
          aside={
            <button
              type="button"
              onClick={() => {/* TODO: open reset modal */}}
              className="border-0 bg-transparent p-0 text-[12px] font-medium text-[#0e9172] transition hover:text-[#0a7a5e] hover:underline"
              tabIndex={-1}
            >
              Forgot password?
            </button>
          }
        >
          <InputWrap leftIcon={<Lock size={16} />}>
            <input
              type={showPw ? 'text' : 'password'}
              required
              minLength={8}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={form.password}
              onChange={onChange('password')}
              className={`${inputCls(!!error)} pr-11`}
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              tabIndex={-1}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 border-0 bg-transparent p-0 text-slate-400 transition hover:text-slate-600"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </InputWrap>
        </Field>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2.5 rounded-[10px] border border-[#c0392b]/20 bg-[#c0392b]/[0.08] px-4 py-3 text-[13.5px] text-[#c0392b]">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={busy}
          className="mt-1 flex h-[46px] w-full items-center justify-center gap-2 rounded-[10px] bg-gradient-to-br from-[#0e9172] to-[#0a7a5e] text-[14px] font-semibold text-white transition hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(14,145,114,0.3)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        >
          {busy
            ? <><Loader2 size={16} className="animate-spin" /> Signing in…</>
            : <>Sign in <ArrowRight size={16} /></>}
        </button>
      </form>
    </AuthShell>
  );
};

/* ── Atoms ────────────────────────────────────────────────── */
const inputCls = (hasError: boolean): string => `
  block h-[46px] w-full rounded-[10px] border-[1.5px] bg-white pl-[42px] pr-3.5
  text-[14px] font-normal text-slate-900 outline-none
  transition focus:border-[#1a5276] focus:ring-[3px] focus:ring-[#1a5276]/10
  ${hasError ? 'border-[#c0392b] focus:border-[#c0392b] focus:ring-[#c0392b]/10' : 'border-slate-200'}
`.replace(/\s+/g, ' ').trim();

const Field: React.FC<{
  label: string;
  icon: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, icon, aside, children }) => (
  <div>
    <div className="mb-2 flex items-center justify-between">
      <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900">
        <span className="text-slate-400">{icon}</span>
        {label}
      </label>
      {aside}
    </div>
    {children}
  </div>
);

const InputWrap: React.FC<{ leftIcon: React.ReactNode; children: React.ReactNode }> = ({ leftIcon, children }) => (
  <div className="group relative">
    <span className="pointer-events-none absolute left-3.5 top-1/2 flex -translate-y-1/2 text-slate-400 transition group-focus-within:text-[#1a5276]">
      {leftIcon}
    </span>
    {children}
  </div>
);

export default LoginPage;
